// Spec: ./AcceptSharedImagesDrawer.spec.md

import { makeStyles } from '@material-ui/styles'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { listTenants } from 'account/components/userManagement/tenants/new-actions'
import { tenantsById } from 'account/components/userManagement/tenants/selectors'
import ApiClient from 'api-client/ApiClient'
import { PendingImage } from 'api-client/Glance'
import { useAppSelector } from 'app/store'
import ListTable from 'core/components/listTable/ListTable'
import Progress from 'core/components/progress/Progress'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import Badge from 'core/elements/badge/Badge'
import Button from 'core/elements/button'
import Modal from 'core/elements/modal/Modal'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import { isAdminRole } from 'core/utils/rbac'
import { listImages } from './actions'
import { acceptImageMemberAction, rejectImageMemberAction } from './imageMembers'

interface Props {
  open: boolean
  onClose: () => void
}

const { glance } = ApiClient.getInstance()

const getStatusVariant = (status: string) => {
  if (status === 'active') return 'success'
  if (status === 'queued') return 'warning'
  return 'unknown'
}

const formatSize = (size: number | null) => {
  if (size == null) return 'N/A'
  const gb = size / (1024 * 1024 * 1024)
  if (gb >= 1) return `${gb.toFixed(1)} GB`
  const mb = size / (1024 * 1024)
  return `${mb.toFixed(1)} MB`
}

const AcceptSharedImagesDrawer: React.FC<Props> = ({ open, onClose }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<PendingImage[]>([])
  const [fetchError, setFetchError] = useState<string | null>(null)

  const { prefs } = useScopedPreferences()
  const currentProjectId = prefs.currentTenant
  const session = useAppSelector((state) => state[sessionStoreKey]) as SessionState
  const isAdmin = useMemo(() => isAdminRole(session), [session])

  useListAction(listTenants)
  const tenantMap = useAppSelector(tenantsById)

  const { update: acceptImage } = useUpdateAction(acceptImageMemberAction)
  const { update: denyImage } = useUpdateAction(rejectImageMemberAction)
  const { reload: reloadImages } = useListAction(listImages)
  const [actionInFlight, setActionInFlight] = useState<string | null>(null)

  const loadImages = useCallback(
    (isMountedRef?: { current: boolean }) => {
      setLoading(true)
      setFetchError(null)
      glance
        .getPendingSharedImages()
        .then((images) => {
          if (!isMountedRef || isMountedRef.current) {
            setRows((images || []).filter((img) => img.owner !== currentProjectId))
          }
        })
        .catch(() => {
          if (!isMountedRef || isMountedRef.current) setFetchError('Failed to load pending images.')
        })
        .finally(() => {
          if (!isMountedRef || isMountedRef.current) setLoading(false)
        })
    },
    [currentProjectId],
  )

  useEffect(() => {
    const ref = { current: true }
    if (open) loadImages(ref)
    return () => {
      ref.current = false
    }
  }, [open, loadImages])

  const handleAccept = useCallback(
    async (imageId: string) => {
      setActionInFlight(imageId)
      try {
        const { success } = await acceptImage({ imageId, memberId: currentProjectId })
        if (success) {
          setRows((prev) => prev.filter((img) => img.id !== imageId))
          reloadImages(true)
        }
      } finally {
        setActionInFlight(null)
      }
    },
    [acceptImage, currentProjectId, reloadImages],
  )

  const handleDeny = useCallback(
    async (imageId: string) => {
      setActionInFlight(imageId)
      try {
        const { success } = await denyImage({ imageId, memberId: currentProjectId })
        if (success) {
          setRows((prev) => prev.filter((img) => img.id !== imageId))
        }
      } finally {
        setActionInFlight(null)
      }
    },
    [denyImage, currentProjectId],
  )

  const columns = useMemo(
    () => [
      { id: 'name', label: 'Name' },
      {
        id: 'status',
        label: (
          <TitleWithInfoIcon
            titleText="Status"
            tooltipMsg="Active means the image is ready to use. Queued means the upload is pending."
          />
        ),
        render: (status: string, row: PendingImage) => (
          <span data-testid={`status-badge-${row.id}`}>
            <Badge text={status} variant={getStatusVariant(status)} />
          </span>
        ),
      },
      {
        id: 'protected',
        label: 'Protected',
        render: (val: boolean) => (val ? 'True' : 'False'),
      },
      { id: 'disk_format', label: 'Disk Format' },
      {
        id: 'owner',
        label: (
          <TitleWithInfoIcon
            titleText="Owner Tenant"
            tooltipMsg="The tenant that owns this image or where this image is created from."
          />
        ),
        render: (ownerId: string) => tenantMap[ownerId]?.name || ownerId || '',
      },
      {
        id: 'virtual_size',
        label: (
          <TitleWithInfoIcon
            titleText="Virtual Disk Size"
            tooltipMsg="The size of the virtual disk that will be created from this image."
          />
        ),
        render: (size: number | null) => formatSize(size),
      },
      {
        id: '_actions',
        label: '',
        render: (_: unknown, row: PendingImage) => (
          <div className={classes.rowActions}>
            <Button
              variant="secondary"
              icon="check"
              onClick={() => handleAccept(row.id)}
              loading={actionInFlight === row.id}
              disabled={!!actionInFlight}
              data-testid={`accept-button-${row.id}`}
            >
              Accept
            </Button>
            <Button
              variant="secondary"
              icon="times"
              onClick={() => handleDeny(row.id)}
              loading={actionInFlight === row.id}
              disabled={!!actionInFlight}
              data-testid={`deny-button-${row.id}`}
            >
              Deny
            </Button>
          </div>
        ),
      },
    ],
    [handleAccept, handleDeny, actionInFlight, tenantMap, classes.rowActions],
  )

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Accept Shared Images"
      panel="drawer"
      slideFrom="right"
      maxWidth={1000}
      footer={
        <Button
          variant="secondary"
          onClick={onClose}
          data-testid="accept-shared-images-close-button"
        >
          Close
        </Button>
      }
    >
      <Progress loading={loading}>
        <Text variant="body2" className={classes.noteText}>
          <strong>Note:</strong> Accept Images only from trusted sources. Deny to remove access.
          {isAdmin &&
            ' By accepting an image it will be available to all other users within the tenant.'}
        </Text>
        {fetchError && <ErrorMessage>{fetchError}</ErrorMessage>}
        {rows.length === 0 && !loading ? (
          <Text variant="body2" data-testid="no-pending-images">
            No pending shared images
          </Text>
        ) : (
          <ListTable
            data={rows}
            columns={columns}
            loading={loading}
            showCheckboxes={false}
            uniqueIdentifier="id"
            searchTargets={['name', 'status', 'disk_format']}
            onRefresh={() => loadImages()}
            noRowsPerPage
            extraToolbarContent={
              <Text variant="subtitle1" className={classes.tableTitle}>
                Shared Images ({rows.length})
              </Text>
            }
            onReload={() => loadImages({ current: true })}
          />
        )}
      </Progress>
    </Modal>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  noteText: {
    color: theme.palette.grey[700],
    marginBottom: theme.spacing(2),
  },
  rowActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  tableTitle: {
    flex: 1,
    fontWeight: 600,
    color: theme.palette.grey[900],
  },
}))

export default AcceptSharedImagesDrawer
